import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import { ChangeEvent, useEffect, useId, useRef, useState } from "react";
import Input from "../../ui/input/Input";
import { RESERVATION_FILTER_KEYS as KEYS } from "./reservationFilters";
import useFilterParams from "./useFilterParams";

const MIN_SEARCH_LENGTH = 3;

interface FilterSearchProps {
  // The URL key the text is written to; the ticket list's by default.
  paramKey?: string;
  placeholder?: string;
  minLength?: number;
}

// Typed locally and written to the URL after a pause, so each keystroke is not a request.
const FilterSearch = ({
  paramKey = KEYS.search,
  placeholder = "Kërko me kod rezervimi, emër, email ose telefon",
  minLength = MIN_SEARCH_LENGTH
}: FilterSearchProps) => {
  const { get, set } = useFilterParams();
  const urlValue = get(paramKey);
  const [value, setValue] = useState(urlValue);

  // Read through refs, so the debounce built once below always writes with the current props.
  const setRef = useRef(set);
  setRef.current = set;
  const keyRef = useRef(paramKey);
  keyRef.current = paramKey;
  const hintId = useId();
  const write = useRef(
    debounce((next: string) => setRef.current({ [keyRef.current]: next.trim() }), 600)
  ).current;
  useEffect(() => () => write.cancel(), [write]);

  // Clear (or browser back) empties the URL; keep the box in step with it.
  useEffect(() => {
    if (!urlValue) setValue("");
  }, [urlValue]);

  // One or two letters match a large share of all passengers and make the slowest possible query,
  // so nothing is sent until there are three (clearing the box still resets the list).
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setValue(next);
    if (next.trim().length === 0 || next.trim().length >= minLength) write(next);
    else write.cancel();
  };

  const onClear = () => {
    write.cancel();
    setValue("");
    set({ [paramKey]: "" });
  };

  const tooShort = value.trim().length > 0 && value.trim().length < minLength;

  return (
    <div className="w-full">
      <Input
        placeholder={placeholder}
        paddingType="fill"
        value={value}
        onChange={onChange}
        aria-describedby={hintId}
        beforeClassName="text-neutral"
        before={<MagnifyingGlassIcon className="w-4 h-4" strokeWidth={2} />}
        after={value ? (
          <button
            type="button"
            onClick={onClear}
            aria-label="Pastro kërkimin"
            // The Input's icon slot ignores the pointer; the button opts back in.
            className="pointer-events-auto rounded-full p-1 text-neutral hover:bg-gray-200 hover:text-black"
          >
            <XMarkIcon className="w-4 h-4" strokeWidth={2} />
          </button>
        ) : undefined}
      />
      {tooShort && (
        <p id={hintId} className="mt-1 text-xs text-neutral">
          Shkruaj të paktën {minLength} shkronja për të kërkuar.
        </p>
      )}
    </div>
  );
};

export default FilterSearch;
